import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';

import {
  ArrowLeft,
  ArrowRight,
  Atom,
  BookOpen,
  Brain,
  Calculator,
  Check,
  CheckCircle,
  Globe,
  GraduationCap,
  Lightning,
  LockOpen,
  Sparkle,
  Target,
  Trophy,
  XCircle,
} from '@phosphor-icons/react';

import SEO from '../components/SEO';
import { trackEvent, EVT } from '../utils/analytics';

const ACCENTS = {
  indigo: {
    chip:
      'bg-indigo-100 text-indigo-700 border-indigo-200 ' +
      'dark:bg-indigo-950/30 dark:text-indigo-300 dark:border-indigo-800',
    border: 'border-indigo-500',
  },
  rose: {
    chip:
      'bg-rose-100 text-rose-700 border-rose-200 ' +
      'dark:bg-rose-950/30 dark:text-rose-300 dark:border-rose-800',
    border: 'border-rose-500',
  },
  emerald: {
    chip:
      'bg-emerald-100 text-emerald-700 border-emerald-200 ' +
      'dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800',
    border: 'border-emerald-500',
  },
  cyan: {
    chip:
      'bg-cyan-100 text-cyan-700 border-cyan-200 ' +
      'dark:bg-cyan-950/30 dark:text-cyan-300 dark:border-cyan-800',
    border: 'border-cyan-500',
  },
};

const normalise = (value = '') =>
  value
    .toLowerCase()
    .replace(/[£,]/g, '')
    .replace(/²/g, '2')
    .replace(/\s+/g, ' ')
    .trim();

const includesAny = (answer, terms) => {
  const cleanAnswer = normalise(answer);

  return terms.some((term) =>
    cleanAnswer.includes(normalise(term)),
  );
};

const LESSONS = {
  maths: {
    label: 'GCSE Maths',
    icon: Calculator,
    accent: 'indigo',
    topic: 'Percentage change',
    subjectPath: '/maths',
    pickerBlurb:
      'Use multipliers for percentage decreases, reverse percentages and repeated change.',
    lesson: {
      headline: 'Percentage change — use a multiplier',
      whyItMatters:
        'Percentage change appears across financial, ratio and real-life GCSE problems. A clear multiplier method is efficient and shows your working.',
      teach: [
        {
          subhead: '1. Convert the change into a multiplier',
          body:
            'Start with 100%. For an increase, add the percentage; for a decrease, subtract it. A 5% increase uses 1.05. A 25% decrease uses 0.75.',
        },
        {
          subhead: '2. Reverse percentages use division',
          body:
            'If £60 is the price after a 25% reduction, £60 represents 75% of the original. Divide by 0.75: £60 ÷ 0.75 = £80.',
        },
        {
          subhead: '3. Repeated change repeats the multiplier',
          body:
            'For the same percentage change over several periods, apply the multiplier once for each period. Two 5% increases use 1.05².',
        },
      ],
      worked: {
        title: 'Worked example',
        question:
          'A jacket costs £80. It is reduced by 25%. What is the sale price?',
        steps: [
          {
            label: 'Step 1: Find the multiplier',
            text:
              '100% − 25% = 75%, so the multiplier is 0.75.',
          },
          {
            label: 'Step 2: Apply it',
            text: '£80 × 0.75',
          },
          {
            label: 'Step 3: Calculate',
            text: '£80 × 0.75 = £60',
          },
        ],
        answer: '£60',
        examinerTip:
          'Write the multiplier clearly before calculating. This shows the method and may allow method marks to be awarded even if the final arithmetic is wrong.',
      },
    },
    questions: [
      {
        id: 'm1',
        type: 'mcq',
        skill: 'Applying a percentage decrease',
        prompt:
          'A pair of trainers costs £90. The price is reduced by 30%. What is the sale price?',
        choices: ['£27', '£60', '£63', '£70'],
        correctIndex: 2,
        explanation:
          'A 30% decrease leaves 70%, so the multiplier is 0.70. £90 × 0.70 = £63.',
      },
      {
        id: 'm2',
        type: 'mcq',
        skill: 'Using a reverse percentage',
        prompt:
          'A laptop is reduced by 20% and now costs £480. What was the original price?',
        choices: ['£500', '£550', '£576', '£600'],
        correctIndex: 3,
        explanation:
          'The sale price is 80% of the original. Divide by 0.80: £480 ÷ 0.80 = £600.',
      },
      {
        id: 'm3',
        type: 'written',
        skill: 'Explaining repeated percentage change',
        prompt:
          'A house is worth £200,000. Its value rises by 5% in each of two years. Show the calculation and give the value after two years.',
        placeholder:
          'Write your multiplier, calculation and final answer…',
        modelAnswer:
          'Multiplier = 1.05. £200,000 × 1.05² = £220,500, so the value after two years is £220,500.',
        rubric: [
          {
            label: 'Identified the multiplier 1.05',
            test: (answer) =>
              includesAny(answer, ['1.05']),
          },
          {
            label: 'Applied the multiplier twice',
            test: (answer) =>
              includesAny(answer, [
                '1.05²',
                '1.05^2',
                '1.05 x 1.05',
                '1.05 × 1.05',
                '1.05 * 1.05',
              ]),
          },
          {
            label: 'Reached £220,500',
            test: (answer) =>
              includesAny(answer, ['220500']),
          },
        ],
      },
    ],
  },

  english: {
    label: 'GCSE English Literature',
    icon: BookOpen,
    accent: 'rose',
    topic: "Macbeth — Lady Macbeth's ambition",
    subjectPath: '/english-language',
    pickerBlurb:
      'Trace Lady Macbeth from commanding ambition to isolation and psychological collapse.',
    lesson: {
      headline: 'Lady Macbeth — ambition, power and collapse',
      whyItMatters:
        'A strong response follows Lady Macbeth across the whole play and keeps returning to Shakespeare’s choices, rather than treating her as a real person.',
      teach: [
        {
          subhead:
            '1. Treat the character as Shakespeare’s construction',
          body:
            'Use phrasing such as “Shakespeare presents Lady Macbeth…” and then explain how language, structure and dramatic form shape the audience’s response.',
        },
        {
          subhead: '2. Trace the whole character arc',
          body:
            'Act 1: commanding, ambitious and transgressive. Act 2: complicit but beginning to show strain. Act 3: increasingly excluded as Macbeth acts alone. Act 5: fragmented by guilt and sleepwalking.',
        },
        {
          subhead:
            '3. Use context as an interpretation, not a fact dump',
          body:
            'A Jacobean audience may have viewed her rejection of expected gender roles and support for regicide as a threat to social and divine order. Other interpretations are valid when supported by the text.',
        },
      ],
      worked: {
        title: 'Worked example',
        question:
          'How could you analyse Lady Macbeth’s command “unsex me here”?',
        steps: [
          {
            label: 'Identify the method',
            text:
              'The imperative verb “unsex” sounds forceful and unnatural.',
          },
          {
            label: 'Develop the interpretation',
            text:
              'She attempts to reject qualities her society associates with femininity so she can pursue power without pity.',
          },
          {
            label: 'Connect to the wider play',
            text:
              'Her later collapse suggests that suppressing guilt does not remove it.',
          },
        ],
        answer:
          'Shakespeare uses the imperative “unsex” to present ambition as a force that drives Lady Macbeth to reject expected gender roles, while her later breakdown exposes the psychological cost of that rejection.',
        examinerTip:
          'Return to Shakespeare’s choices throughout the paragraph: what he presents, how he presents it and why that presentation matters.',
      },
    },
    questions: [
      {
        id: 'e1',
        type: 'mcq',
        skill: 'Recognising writer-focused analysis',
        prompt:
          'Which opening most clearly treats Lady Macbeth as a character constructed by the writer?',
        choices: [
          'Lady Macbeth is evil and wants power.',
          'Shakespeare presents Lady Macbeth as commanding and transgressive in Act 1.',
          'Lady Macbeth feels very ambitious.',
          'Lady Macbeth was Macbeth’s wife.',
        ],
        correctIndex: 1,
        explanation:
          'The strongest option foregrounds Shakespeare and gives a precise interpretation rather than a simple description.',
      },
      {
        id: 'e2',
        type: 'mcq',
        skill: 'Tracing a character arc',
        prompt:
          'Which summary most accurately traces Lady Macbeth across the play?',
        choices: [
          'Weak, then powerful, then forgiving',
          'Ambitious and commanding, then increasingly excluded, then psychologically fragmented',
          'Innocent, then loyal, then victorious',
          'Cruel throughout with no meaningful change',
        ],
        correctIndex: 1,
        explanation:
          'A whole-play response should show how her early authority gives way to exclusion, guilt and collapse.',
      },
      {
        id: 'e3',
        type: 'written',
        skill: 'Analysing language and interpretation',
        prompt:
          'Write two or three sentences analysing the words “unsex me here”. Refer to Shakespeare’s method and what the line suggests about Lady Macbeth.',
        placeholder:
          'For example: Shakespeare uses the imperative…',
        modelAnswer:
          'Shakespeare uses the imperative verb “unsex” to present Lady Macbeth as forcefully rejecting the gender expectations of her society. The unnatural command suggests that her ambition drives her to suppress compassion, while her later breakdown reveals the cost of trying to deny guilt.',
        rubric: [
          {
            label: 'Foregrounded Shakespeare or the writer',
            test: (answer) =>
              includesAny(answer, [
                'shakespeare',
                'writer',
                'playwright',
              ]),
          },
          {
            label: 'Analysed a method or word choice',
            test: (answer) =>
              includesAny(answer, [
                'imperative',
                'verb',
                'command',
                'word',
                'language',
              ]),
          },
          {
            label: 'Developed an interpretation',
            test: (answer) =>
              includesAny(answer, [
                'gender',
                'feminin',
                'ambition',
                'power',
                'compassion',
                'guilt',
                'transgress',
              ]),
          },
        ],
      },
    ],
  },

  science: {
    label: 'GCSE Combined Science',
    icon: Atom,
    accent: 'emerald',
    topic: 'Forces — Newton’s second law',
    subjectPath: '/combined-science',
    pickerBlurb:
      'Use resultant force, mass and acceleration accurately with F = ma.',
    lesson: {
      headline: 'Newton’s second law — F = ma',
      whyItMatters:
        'Newton’s second law is an important GCSE relationship used in calculations and explanations involving resultant force, mass and acceleration.',
      teach: [
        {
          subhead: '1. Start with the resultant force',
          body:
            'The force in F = ma is the resultant force measured in newtons. If forces act in opposite directions, subtract the smaller from the larger first.',
        },
        {
          subhead:
            '2. Use kilograms and metres per second squared',
          body:
            'Mass must be in kilograms and acceleration in m/s². Convert grams to kilograms before substituting.',
        },
        {
          subhead:
            '3. Show equation, substitution and units',
          body:
            'Write F = ma, substitute the values, calculate, and include the correct unit. Clear working may earn method marks even when the final answer is wrong.',
        },
      ],
      worked: {
        title: 'Worked example',
        question:
          'A trolley of mass 1,500 kg accelerates at 2 m/s². What is the resultant force?',
        steps: [
          {
            label: 'Step 1: Write the equation',
            text: 'F = m × a',
          },
          {
            label: 'Step 2: Substitute',
            text: 'F = 1,500 kg × 2 m/s²',
          },
          {
            label: 'Step 3: Calculate',
            text: 'F = 3,000 N',
          },
        ],
        answer: '3,000 N',
        examinerTip:
          'Check that the force is resultant, the mass is in kilograms and the final unit is newtons.',
      },
    },
    questions: [
      {
        id: 's1',
        type: 'mcq',
        skill: 'Calculating force',
        prompt:
          'A car of mass 1,000 kg accelerates at 2 m/s². What is the resultant force?',
        choices: [
          '500 N',
          '1,000 N',
          '2,000 N',
          '4,000 N',
        ],
        correctIndex: 2,
        explanation:
          'F = m × a = 1,000 × 2 = 2,000 N.',
      },
      {
        id: 's2',
        type: 'mcq',
        skill: 'Rearranging F = ma',
        prompt:
          'The resultant force on a 250 kg box is 500 N. What is its acceleration?',
        choices: [
          '0.5 m/s²',
          '2 m/s²',
          '125,000 m/s²',
          '750 m/s²',
        ],
        correctIndex: 1,
        explanation:
          'Rearrange to a = F ÷ m. Then 500 ÷ 250 = 2 m/s².',
      },
      {
        id: 's3',
        type: 'written',
        skill: 'Using resultant force before F = ma',
        prompt:
          'A car of mass 800 kg has a 2,000 N driving force and a 400 N resistive force. Calculate its acceleration. Show both steps and include the unit.',
        placeholder:
          'Resultant force = … Then a = …',
        modelAnswer:
          'Resultant force = 2,000 − 400 = 1,600 N. a = F ÷ m = 1,600 ÷ 800 = 2 m/s².',
        rubric: [
          {
            label: 'Found the resultant force of 1,600 N',
            test: (answer) =>
              includesAny(answer, ['1600']),
          },
          {
            label: 'Used division by the mass',
            test: (answer) =>
              includesAny(answer, [
                '1600 ÷ 800',
                '1600/800',
                '1600 / 800',
                'f/m',
                'f ÷ m',
              ]),
          },
          {
            label: 'Reached 2 m/s²',
            test: (answer) =>
              includesAny(answer, [
                '2 m/s2',
                '2m/s2',
                '2 metres per second squared',
              ]),
          },
        ],
      },
    ],
  },

  geography: {
    label: 'GCSE Geography',
    icon: Globe,
    accent: 'cyan',
    topic: 'Coastal erosion',
    subjectPath: '/subjects/geography',
    pickerBlurb:
      'Distinguish hydraulic action and abrasion, then explain why erosion rates vary.',
    lesson: {
      headline: 'Coastal erosion — process and explanation',
      whyItMatters:
        'Strong geography answers do more than name a process. They connect wave energy, geology and repeated action to explain how a coastline changes.',
      teach: [
        {
          subhead: '1. Hydraulic action',
          body:
            'Waves force water and air into cracks. Repeated compression can weaken the rock and make fragments break away.',
        },
        {
          subhead: '2. Abrasion',
          body:
            'Rock fragments carried by waves are thrown or scraped against the cliff, wearing the surface away.',
        },
        {
          subhead: '3. Explain why erosion varies',
          body:
            'Erosion is usually faster where waves are energetic, rock is less resistant, or joints and faults provide weaknesses. Sheltered locations and resistant rock generally erode more slowly.',
        },
      ],
      worked: {
        title: 'Worked example',
        question:
          'Explain why a jointed cliff exposed to powerful waves may erode quickly.',
        steps: [
          {
            label: 'Identify the weakness',
            text:
              'Joints provide cracks that waves can enter.',
          },
          {
            label: 'Apply the process',
            text:
              'Hydraulic action repeatedly compresses air and water inside the cracks.',
          },
          {
            label: 'Explain the outcome',
            text:
              'The cracks widen, the rock weakens and fragments eventually break away.',
          },
        ],
        answer:
          'Powerful waves intensify hydraulic action in the joints. Repeated compression widens the cracks and weakens the cliff, so rock breaks away more rapidly.',
        examinerTip:
          'Build a chain of reasoning: condition → process → physical change → outcome.',
      },
    },
    questions: [
      {
        id: 'g1',
        type: 'mcq',
        skill: 'Identifying hydraulic action',
        prompt:
          'Which description best matches hydraulic action?',
        choices: [
          'Rock fragments scrape against the cliff',
          'Water dissolves all types of rock',
          'Air and water are forced into cracks by waves',
          'Material is carried along the beach by longshore drift',
        ],
        correctIndex: 2,
        explanation:
          'Hydraulic action occurs when wave pressure forces air and water into weaknesses in the rock.',
      },
      {
        id: 'g2',
        type: 'mcq',
        skill: 'Distinguishing abrasion',
        prompt:
          'Which process involves wave-carried sediment striking or scraping the cliff?',
        choices: [
          'Abrasion',
          'Deposition',
          'Weathering',
          'Mass movement',
        ],
        correctIndex: 0,
        explanation:
          'Abrasion is the wearing away of the cliff by material carried by waves.',
      },
      {
        id: 'g3',
        type: 'written',
        skill: 'Building a geographical explanation',
        prompt:
          'Explain why a soft, jointed cliff facing high-energy waves may erode quickly. Write two or three linked sentences.',
        placeholder:
          'The cliff may erode quickly because…',
        modelAnswer:
          'The soft rock offers little resistance and the joints provide weaknesses. High-energy waves force air and water into the cracks through hydraulic action, widening them until fragments break away. Abrasion by wave-carried sediment can then wear the exposed cliff further.',
        rubric: [
          {
            label:
              'Used a relevant condition such as soft rock, joints or high-energy waves',
            test: (answer) =>
              includesAny(answer, [
                'soft',
                'joint',
                'crack',
                'high-energy',
                'powerful wave',
                'wave energy',
              ]),
          },
          {
            label: 'Applied an erosion process',
            test: (answer) =>
              includesAny(answer, [
                'hydraulic action',
                'abrasion',
                'compress',
                'scrape',
              ]),
          },
          {
            label: 'Explained the physical outcome',
            test: (answer) =>
              includesAny(answer, [
                'break away',
                'weaken',
                'widen',
                'erode quickly',
                'collapse',
                'wear away',
              ]),
          },
        ],
      },
    ],
  },
};

function Reveal({
  children,
  className = '',
  delay = 0,
}) {
  const reduceMotion = useReducedMotion();

  return (
    <motion.div
      initial={
        reduceMotion
          ? false
          : {
              opacity: 0,
              y: 16,
            }
      }
      whileInView={
        reduceMotion
          ? undefined
          : {
              opacity: 1,
              y: 0,
            }
      }
      viewport={{
        once: true,
        amount: 0.15,
      }}
      transition={{
        duration: 0.35,
        delay,
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function ProgressSteps({
  stage,
  questionIndex = 0,
  questionTotal = 3,
}) {
  const stages = [
    {
      id: 'pick',
      label: 'Choose',
    },
    {
      id: 'lesson',
      label: 'Learn',
    },
    {
      id: 'questions',
      label: 'Practise',
    },
    {
      id: 'result',
      label: 'Result',
    },
  ];

  const currentIndex = stages.findIndex(
    (item) => item.id === stage,
  );

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
      <ol
        className="grid grid-cols-4 gap-2"
        aria-label="Free lesson progress"
      >
        {stages.map((item, index) => {
          const complete = index < currentIndex;
          const active = index === currentIndex;

          return (
            <li key={item.id}>
              <div
                className={`h-2 rounded-full border border-foreground/10 ${
                  complete || active
                    ? 'bg-primary'
                    : 'bg-secondary'
                }`}
                aria-hidden="true"
              />

              <p
                className={`mt-1.5 text-[10px] sm:text-xs font-black uppercase tracking-wide ${
                  active
                    ? 'text-foreground'
                    : 'text-muted-foreground'
                }`}
              >
                {item.label}

                {active && stage === 'questions'
                  ? ` ${questionIndex + 1}/${questionTotal}`
                  : ''}
              </p>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function SubjectPicker({
  onPick,
  headingRef,
}) {
  return (
    <>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary border border-primary/20 text-[11px] font-black uppercase tracking-wide mb-5">
          <Sparkle size={12} weight="fill" />
          No signup · Four subjects · About 8 minutes
        </div>

        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black leading-[1.05] mb-4 max-w-5xl outline-none"
          data-testid="free-lesson-h1"
        >
          Try a real GCSE lesson before creating an account
        </h1>

        <p className="text-lg text-muted-foreground max-w-3xl leading-relaxed">
          Choose a subject, learn one focused concept and answer
          three aligned questions. The final task includes a
          written-response feedback preview so you can see how
          Grade9Ready turns an answer into a next step.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5"
          data-testid="free-lesson-subject-picker"
        >
          {Object.entries(LESSONS).map(
            ([key, subject], index) => {
              const Icon = subject.icon;
              const accent = ACCENTS[subject.accent];

              return (
                <Reveal
                  key={key}
                  delay={index * 0.04}
                  className="h-full"
                >
                  <button
                    type="button"
                    onClick={() => {
                      trackEvent(
                        EVT.FREE_LESSON_START,
                        {
                          subject: key,
                        },
                      );

                      onPick(key);
                    }}
                    data-testid={`free-lesson-pick-${key}`}
                    className={`neo-card h-full w-full p-6 text-left hover:-translate-y-1 transition-transform border-t-4 ${accent.border}`}
                  >
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center border mb-4 ${accent.chip}`}
                    >
                      <Icon size={23} weight="bold" />
                    </div>

                    <h2 className="font-heading font-black text-xl mb-1">
                      {subject.label}
                    </h2>

                    <p className="text-[11px] font-black text-muted-foreground uppercase tracking-wide mb-3">
                      {subject.topic}
                    </p>

                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {subject.pickerBlurb}
                    </p>

                    <span className="mt-5 inline-flex items-center gap-1 text-sm font-black text-primary">
                      Start lesson
                      <ArrowRight
                        size={15}
                        weight="bold"
                      />
                    </span>
                  </button>
                </Reveal>
              );
            },
          )}
        </div>
      </section>
    </>
  );
}

function LessonStage({
  subjectKey,
  onContinue,
  onBack,
  headingRef,
}) {
  const subject = LESSONS[subjectKey];
  const accent = ACCENTS[subject.accent];
  const Icon = subject.icon;

  return (
    <section
      className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16"
      data-testid="free-lesson-stage-lesson"
    >
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm font-bold text-muted-foreground hover:text-foreground mb-6"
      >
        <ArrowLeft size={16} weight="bold" />
        Choose another subject
      </button>

      <div className="flex items-center gap-3 mb-5">
        <div
          className={`w-11 h-11 rounded-xl border flex items-center justify-center ${accent.chip}`}
        >
          <Icon size={22} weight="bold" />
        </div>

        <span
          className={`inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wide px-2.5 py-1 rounded-full border ${accent.chip}`}
        >
          <GraduationCap size={11} weight="fill" />
          {subject.label} · Learn
        </span>
      </div>

      <h1
        ref={headingRef}
        tabIndex={-1}
        className="text-3xl sm:text-4xl font-heading font-black mb-3 leading-tight outline-none"
        data-testid="free-lesson-headline"
      >
        {subject.lesson.headline}
      </h1>

      <p
        className="text-base text-muted-foreground leading-relaxed mb-8"
        data-testid="free-lesson-why"
      >
        <strong className="text-foreground">
          Why it matters:
        </strong>{' '}
        {subject.lesson.whyItMatters}
      </p>

      <div
        className="space-y-4 mb-8"
        data-testid="free-lesson-teach"
      >
        {subject.lesson.teach.map((block) => (
          <div
            key={block.subhead}
            className="neo-card p-5"
          >
            <h2 className="font-heading font-black text-base mb-2">
              {block.subhead}
            </h2>

            <p className="text-sm leading-relaxed text-muted-foreground">
              {block.body}
            </p>
          </div>
        ))}
      </div>

      <div
        className="neo-card p-6 bg-amber-50 dark:bg-amber-950/15 border-amber-300"
        data-testid="free-lesson-worked"
      >
        <p className="text-[11px] font-black uppercase tracking-wide text-amber-700 dark:text-amber-300 mb-2 flex items-center gap-1.5">
          <Sparkle size={12} weight="fill" />
          {subject.lesson.worked.title}
        </p>

        <h2 className="font-heading font-black text-lg mb-4 leading-snug">
          {subject.lesson.worked.question}
        </h2>

        <ol className="space-y-3 mb-4">
          {subject.lesson.worked.steps.map((step) => (
            <li
              key={step.label}
              className="rounded-xl border-2 border-amber-200/70 bg-card p-3"
            >
              <p className="text-[10px] font-black uppercase tracking-wide text-amber-700 dark:text-amber-300 mb-1">
                {step.label}
              </p>

              <p className="text-sm font-bold">
                {step.text}
              </p>
            </li>
          ))}
        </ol>

        <div className="rounded-xl bg-emerald-100 dark:bg-emerald-950/20 border-2 border-emerald-300 p-3 mb-3">
          <p className="text-[10px] font-black uppercase tracking-wide text-emerald-700 dark:text-emerald-300 mb-1 flex items-center gap-1">
            <CheckCircle size={11} weight="fill" />
            Strong answer
          </p>

          <p className="font-heading font-black text-base">
            {subject.lesson.worked.answer}
          </p>
        </div>

        <div className="rounded-xl bg-fuchsia-50 dark:bg-fuchsia-950/15 border-2 border-fuchsia-200 p-3">
          <p className="text-[10px] font-black uppercase tracking-wide text-fuchsia-700 dark:text-fuchsia-300 mb-1 flex items-center gap-1">
            <Trophy size={11} weight="fill" />
            Exam tip
          </p>

          <p className="text-sm leading-relaxed">
            {subject.lesson.worked.examinerTip}
          </p>
        </div>
      </div>

      <button
        type="button"
        onClick={() => {
          trackEvent(
            'free_lesson_content_complete',
            {
              subject: subjectKey,
            },
          );

          onContinue();
        }}
        data-testid="free-lesson-start-questions"
        className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-primary text-primary-foreground font-heading font-black shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))] hover:-translate-y-0.5 transition-transform"
      >
        Start the three questions
        <ArrowRight size={17} weight="bold" />
      </button>

      <p className="text-xs text-muted-foreground mt-3">
        The questions test only the ideas taught above.
      </p>
    </section>
  );
}

function evaluateQuestion(
  question,
  selectedIndex,
  writtenAnswer,
) {
  if (question.type === 'mcq') {
    const correct =
      selectedIndex === question.correctIndex;

    return {
      id: question.id,
      skill: question.skill,
      earned: correct ? 1 : 0,
      possible: 1,
      correct,
      explanation: question.explanation,
      whatWentWell: correct
        ? `You applied ${question.skill.toLowerCase()} correctly.`
        : null,
      evenBetterIf: correct
        ? null
        : question.explanation,
    };
  }

  const criteria = question.rubric.map(
    (criterion) => ({
      ...criterion,
      met: criterion.test(writtenAnswer),
    }),
  );

  const earned = criteria.filter(
    (criterion) => criterion.met,
  ).length;

  const possible = criteria.length;

  return {
    id: question.id,
    skill: question.skill,
    earned,
    possible,
    correct: earned === possible,
    criteria,
    whatWentWell: criteria
      .filter((criterion) => criterion.met)
      .map((criterion) => criterion.label),
    evenBetterIf: criteria
      .filter((criterion) => !criterion.met)
      .map((criterion) => criterion.label),
    modelAnswer: question.modelAnswer,
  };
}

function QuestionStage({
  subjectKey,
  question,
  questionIndex,
  total,
  onComplete,
  onBack,
  headingRef,
}) {
  const subject = LESSONS[subjectKey];
  const accent = ACCENTS[subject.accent];

  const [selectedIndex, setSelectedIndex] =
    useState(null);

  const [writtenAnswer, setWrittenAnswer] =
    useState('');

  const [result, setResult] = useState(null);

  const feedbackRef = useRef(null);

  useEffect(() => {
    setSelectedIndex(null);
    setWrittenAnswer('');
    setResult(null);
  }, [question.id]);

  useEffect(() => {
    if (result) {
      feedbackRef.current?.focus();
    }
  }, [result]);

  const canSubmit =
    question.type === 'mcq'
      ? selectedIndex !== null
      : writtenAnswer.trim().length >= 12;

  const submit = () => {
    if (!canSubmit || result) {
      return;
    }

    const nextResult = evaluateQuestion(
      question,
      selectedIndex,
      writtenAnswer,
    );

    setResult(nextResult);

    trackEvent(EVT.FREE_LESSON_ANSWER, {
      subject: subjectKey,
      qid: question.id,
      correct: nextResult.correct,
      earned: nextResult.earned,
      possible: nextResult.possible,
      type: question.type,
    });
  };

  return (
    <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <span
          className={`inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wide px-2.5 py-1 rounded-full border ${accent.chip}`}
        >
          <Lightning size={11} weight="fill" />
          {subject.label} · Practise
        </span>

        <button
          type="button"
          onClick={onBack}
          className="text-xs font-bold text-muted-foreground hover:text-foreground"
          data-testid="free-lesson-change-subject"
        >
          Change subject
        </button>
      </div>

      <div
        className="neo-card p-6 sm:p-8"
        data-testid={`free-lesson-question-${question.id}`}
      >
        <p className="text-[11px] font-black uppercase tracking-wide text-muted-foreground mb-3">
          Question {questionIndex + 1} of {total} ·{' '}
          {question.skill}
        </p>

        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-xl sm:text-2xl font-heading font-black leading-snug mb-6 outline-none"
          data-testid="free-lesson-stem"
        >
          {question.prompt}
        </h1>

        {question.type === 'mcq' ? (
          <div
            role="radiogroup"
            aria-label={`Question ${
              questionIndex + 1
            } answer choices`}
            className="space-y-3"
          >
            {question.choices.map(
              (choice, index) => {
                const selected =
                  selectedIndex === index;

                const correctChoice =
                  result &&
                  index === question.correctIndex;

                const wrongSelected =
                  result &&
                  selected &&
                  !correctChoice;

                let classes =
                  'border-foreground/15 hover:border-foreground/40';

                if (result) {
                  if (correctChoice) {
                    classes =
                      'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/20';
                  } else if (wrongSelected) {
                    classes =
                      'border-rose-500 bg-rose-50 dark:bg-rose-950/20';
                  } else {
                    classes =
                      'border-foreground/10 opacity-60';
                  }
                } else if (selected) {
                  classes =
                    'border-primary bg-primary/5';
                }

                return (
                  <button
                    key={choice}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled={Boolean(result)}
                    onClick={() =>
                      setSelectedIndex(index)
                    }
                    data-testid={`free-lesson-choice-${question.id}-${index}`}
                    className={`w-full text-left p-3.5 rounded-xl border-2 font-bold text-sm transition-all flex items-center justify-between gap-3 ${classes}`}
                  >
                    <span>{choice}</span>

                    {correctChoice && (
                      <CheckCircle
                        size={18}
                        weight="fill"
                        className="text-emerald-600 shrink-0"
                        aria-label="Correct answer"
                      />
                    )}

                    {wrongSelected && (
                      <XCircle
                        size={18}
                        weight="fill"
                        className="text-rose-600 shrink-0"
                        aria-label="Your answer was not correct"
                      />
                    )}
                  </button>
                );
              },
            )}
          </div>
        ) : (
          <div>
            <label
              htmlFor={`written-answer-${question.id}`}
              className="sr-only"
            >
              Your written answer
            </label>

            <textarea
              id={`written-answer-${question.id}`}
              value={writtenAnswer}
              onChange={(event) =>
                setWrittenAnswer(event.target.value)
              }
              disabled={Boolean(result)}
              rows={6}
              maxLength={900}
              placeholder={question.placeholder}
              className="neo-input w-full resize-y leading-relaxed"
              data-testid="free-lesson-written-answer"
            />

            <div className="flex items-center justify-between gap-4 mt-2 text-xs text-muted-foreground">
              <span>
                Write at least one complete sentence.
              </span>

              <span>
                {writtenAnswer.length}/900
              </span>
            </div>
          </div>
        )}

        {!result && (
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            data-testid="free-lesson-submit"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-primary text-primary-foreground font-heading font-black shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none hover:-translate-y-0.5 transition-transform"
          >
            Check answer
            <ArrowRight size={16} weight="bold" />
          </button>
        )}

        {result && (
          <div
            ref={feedbackRef}
            tabIndex={-1}
            role="status"
            aria-live="polite"
            className="mt-6 rounded-2xl border-2 border-primary/30 bg-primary/5 p-5 outline-none"
            data-testid="free-lesson-feedback"
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <p className="text-[11px] font-black uppercase tracking-wide text-primary mb-1.5 flex items-center gap-1.5">
                  <Brain size={13} weight="fill" />
                  Grade9Ready feedback preview
                </p>

                <h2 className="font-heading font-black text-xl">
                  {result.earned === result.possible
                    ? 'Strong response'
                    : result.earned > 0
                      ? 'Good start — there is another improvement to make'
                      : 'Not yet — use the feedback and try the method again'}
                </h2>
              </div>

              <span className="rounded-full border-2 border-foreground px-3 py-1 text-sm font-black shrink-0">
                {result.earned}/{result.possible}
              </span>
            </div>

            {question.type === 'mcq' ? (
              <div>
                <p className="font-bold mb-1">
                  {result.correct
                    ? 'Correct.'
                    : 'Not quite.'}
                </p>

                <p className="text-sm leading-relaxed text-muted-foreground">
                  {result.explanation}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 dark:bg-emerald-950/15 p-4">
                  <p className="text-[10px] font-black uppercase tracking-wide text-emerald-700 dark:text-emerald-300 mb-2">
                    What went well
                  </p>

                  {result.whatWentWell.length > 0 ? (
                    <ul className="space-y-1.5 text-sm">
                      {result.whatWentWell.map(
                        (item) => (
                          <li
                            key={item}
                            className="flex items-start gap-2"
                          >
                            <Check
                              size={15}
                              weight="bold"
                              className="text-emerald-600 mt-0.5 shrink-0"
                            />

                            {item}
                          </li>
                        ),
                      )}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      You attempted the task. Compare
                      your answer with the model and add
                      the missing reasoning.
                    </p>
                  )}
                </div>

                <div className="rounded-xl border-2 border-amber-200 bg-amber-50 dark:bg-amber-950/15 p-4">
                  <p className="text-[10px] font-black uppercase tracking-wide text-amber-700 dark:text-amber-300 mb-2">
                    Even better if
                  </p>

                  {result.evenBetterIf.length > 0 ? (
                    <ul className="space-y-1.5 text-sm">
                      {result.evenBetterIf.map(
                        (item) => (
                          <li
                            key={item}
                            className="flex items-start gap-2"
                          >
                            <Target
                              size={15}
                              weight="bold"
                              className="text-amber-700 mt-0.5 shrink-0"
                            />

                            {item}
                          </li>
                        ),
                      )}
                    </ul>
                  ) : (
                    <p className="text-sm">
                      All three checkpoints were
                      present. Improve further by making
                      the explanation more precise and
                      concise.
                    </p>
                  )}
                </div>

                <details className="rounded-xl border-2 border-foreground/10 bg-card p-4">
                  <summary className="cursor-pointer font-black text-sm">
                    Compare with a model response
                  </summary>

                  <p className="text-sm text-muted-foreground leading-relaxed mt-3">
                    {result.modelAnswer}
                  </p>
                </details>

                <p className="text-xs text-muted-foreground">
                  This no-signup preview uses transparent
                  answer checkpoints. The signed-in
                  platform provides fuller AI feedback
                  and records progress.
                </p>
              </div>
            )}

            <button
              type="button"
              onClick={() => onComplete(result)}
              data-testid="free-lesson-next"
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl border-2 border-foreground font-heading font-black hover:bg-secondary transition-colors"
            >
              {questionIndex + 1 >= total
                ? 'See my result'
                : 'Next question'}

              <ArrowRight size={16} weight="bold" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

function ResultStage({
  subjectKey,
  results,
  onTryAnother,
  headingRef,
}) {
  const subject = LESSONS[subjectKey];

  const earned = results.reduce(
    (sum, result) => sum + result.earned,
    0,
  );

  const possible = results.reduce(
    (sum, result) => sum + result.possible,
    0,
  );

  const percentage = possible
    ? Math.round((earned / possible) * 100)
    : 0;

  const strongest =
    results
      .filter((result) => result.earned > 0)
      .sort(
        (first, second) =>
          second.earned / second.possible -
          first.earned / first.possible,
      )[0]?.skill ||
    'Completing the lesson pathway';

  const nextFocus =
    results.find(
      (result) => result.earned < result.possible,
    )?.skill ||
    'Applying the skill in a longer exam-style task';

  const tone =
    percentage >= 80
      ? {
          label: 'Strong understanding',
          body:
            'You applied most of the lesson accurately. Your next step is to use the same skill in a longer exam-style task.',
        }
      : percentage >= 50
        ? {
            label: 'Good foundation',
            body:
              'You have understood part of the method. Use the feedback to correct the missing step before moving on.',
          }
        : {
            label: 'A useful starting point',
            body:
              'The result has identified a clear area to revisit. That is exactly what effective revision should do.',
          };

  return (
    <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-20">
      <div
        className="neo-card p-7 sm:p-10 bg-gradient-to-br from-primary/5 to-primary/10 border-primary/30"
        data-testid="free-lesson-signup-gate"
      >
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-5 mb-7">
          <div>
            <p className="text-[11px] font-black uppercase tracking-wide text-primary mb-2 flex items-center gap-1.5">
              <Trophy size={13} weight="fill" />
              {tone.label}
            </p>

            <h1
              ref={headingRef}
              tabIndex={-1}
              className="font-heading font-black text-3xl sm:text-4xl mb-3 outline-none"
              data-testid="free-lesson-gate-score"
            >
              You earned {earned} of {possible}{' '}
              checkpoints in {subject.label}
            </h1>

            <p className="text-muted-foreground leading-relaxed max-w-2xl">
              {tone.body}
            </p>
          </div>

          <div className="w-24 h-24 rounded-full border-4 border-primary bg-card flex items-center justify-center shrink-0">
            <span className="font-heading font-black text-2xl">
              {percentage}%
            </span>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 dark:bg-emerald-950/15 p-4">
            <p className="text-[10px] font-black uppercase tracking-wide text-emerald-700 dark:text-emerald-300 mb-2">
              Strongest area
            </p>

            <p className="font-bold">{strongest}</p>
          </div>

          <div className="rounded-xl border-2 border-amber-200 bg-amber-50 dark:bg-amber-950/15 p-4">
            <p className="text-[10px] font-black uppercase tracking-wide text-amber-700 dark:text-amber-300 mb-2">
              Best next focus
            </p>

            <p className="font-bold">{nextFocus}</p>
          </div>
        </div>

        <div className="border-t-2 border-foreground/10 pt-7">
          <h2 className="font-heading font-black text-2xl mb-3">
            Create your free account to continue
          </h2>

          <p className="text-muted-foreground leading-relaxed mb-5">
            You scored {earned}/{possible} in {subject.label}.
            No card required — unlock the full pathway across
            Maths, English, Science and Geography in seconds.
          </p>

          <div className="grid sm:grid-cols-2 gap-3 mb-6">
            {[
              'No payment card required',
              '7 days full access, cancel anytime',
              '5 AI marking credits included',
            ].map((item) => (
              <div
                key={item}
                className="flex items-start gap-2 text-sm font-bold"
              >
                <CheckCircle
                  size={16}
                  weight="fill"
                  className="text-emerald-500 mt-0.5 shrink-0"
                />

                <span>{item}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row flex-wrap gap-3">
            <Link
              to={`/signup?role=parent&source=free_lesson&subject=${subjectKey}&score=${earned}&total=${possible}`}
              onClick={() =>
                trackEvent(EVT.CTA_CLICK, {
                  cta: 'start_free_sampler',
                  location: 'free_lesson_result',
                  subject: subjectKey,
                  score: earned,
                  total: possible,
                })
              }
              data-testid="free-lesson-cta-signup"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-primary text-primary-foreground font-heading font-black shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))] hover:-translate-y-0.5 transition-transform"
            >
              <LockOpen size={18} weight="bold" />
              Create Parent Account
            </Link>

            <button
              type="button"
              onClick={onTryAnother}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl border-2 border-foreground font-heading font-black hover:bg-secondary transition-colors"
            >
              Try another subject
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function FreeLesson() {
  const [searchParams] = useSearchParams();
  const requestedSubject = searchParams.get('subject');
  const initialSubject = LESSONS[requestedSubject] ? requestedSubject : null;
  const [stage, setStage] = useState(initialSubject ? 'lesson' : 'pick');
  const [subjectKey, setSubjectKey] = useState(initialSubject);
  const [questionIndex, setQuestionIndex] =
    useState(0);
  const [results, setResults] = useState([]);

  const headingRef = useRef(null);

  const subject = subjectKey
    ? LESSONS[subjectKey]
    : null;

  const currentQuestion =
    subject && stage === 'questions'
      ? subject.questions[questionIndex]
      : null;

  const questionTotal =
    subject?.questions.length || 3;

  const currentScore = useMemo(
    () =>
      results.reduce(
        (sum, result) => sum + result.earned,
        0,
      ),
    [results],
  );

  const currentPossible = useMemo(
    () =>
      results.reduce(
        (sum, result) => sum + result.possible,
        0,
      ),
    [results],
  );

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'auto',
    });

    window.setTimeout(() => {
      headingRef.current?.focus();
    }, 0);
  }, [stage, questionIndex]);

  const reset = () => {
    setStage('pick');
    setSubjectKey(null);
    setQuestionIndex(0);
    setResults([]);
  };

  const chooseSubject = (key) => {
    setSubjectKey(key);
    setQuestionIndex(0);
    setResults([]);
    setStage('lesson');
  };

  const completeQuestion = (result) => {
    const nextResults = [
      ...results,
      result,
    ];

    const lastQuestion =
      questionIndex + 1 >= questionTotal;

    setResults(nextResults);

    if (!lastQuestion) {
      setQuestionIndex(
        (index) => index + 1,
      );

      return;
    }

    const earned = nextResults.reduce(
      (sum, item) => sum + item.earned,
      0,
    );

    const possible = nextResults.reduce(
      (sum, item) => sum + item.possible,
      0,
    );

    trackEvent(EVT.FREE_LESSON_GATE, {
      subject: subjectKey,
      score: earned,
      total: possible,
    });

    trackEvent(EVT.FREE_LESSON_COMPLETE, {
      subject: subjectKey,
      score: earned,
      total: possible,
    });

    trackEvent(EVT.LESSON_COMPLETED, {
      subject: subjectKey,
      score: earned,
      total: possible,
      source: 'free_lesson',
    });

    try {
      localStorage.setItem(
        'g9r_free_lesson_result',
        JSON.stringify({
          completed: true,
          subject: subjectKey,
          score: earned,
          total: possible,
          percentage: possible
            ? Math.round(
                (earned / possible) * 100,
              )
            : 0,
          completedAt:
            new Date().toISOString(),
        }),
      );
    } catch (_error) {
      // Storage may be unavailable in privacy-restricted browsers.
    }

    setStage('result');
  };

  return (
    <div
      className="min-h-screen bg-background"
      data-testid="free-lesson-page"
    >
      <SEO
        title="Try a Free GCSE Lesson with Feedback | Grade9Ready"
        description="Try a GCSE Maths, English, Science or Geography mini-lesson with aligned questions and a written-feedback preview. No signup required."
        path="/free-lesson"
      />

      <nav
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 text-xs text-muted-foreground"
        aria-label="Breadcrumb"
      >
        <Link
          to="/"
          className="hover:text-foreground"
        >
          Home
        </Link>

        <span
          className="mx-2"
          aria-hidden="true"
        >
          /
        </span>

        <span className="text-foreground font-bold">
          Free Lesson
        </span>
      </nav>

      <ProgressSteps
        stage={stage}
        questionIndex={questionIndex}
        questionTotal={questionTotal}
      />

      <div
        className="sr-only"
        aria-live="polite"
        aria-atomic="true"
      >
        {stage === 'pick' &&
          'Choose a subject.'}

        {stage === 'lesson' &&
          subject &&
          `${subject.label} lesson opened.`}

        {stage === 'questions' &&
          subject &&
          `Question ${
            questionIndex + 1
          } of ${questionTotal} in ${
            subject.label
          }. Current score ${currentScore} of ${currentPossible}.`}

        {stage === 'result' &&
          'Free lesson complete. Your result is ready.'}
      </div>

      {stage === 'pick' && (
        <SubjectPicker
          onPick={chooseSubject}
          headingRef={headingRef}
        />
      )}

      {stage === 'lesson' && subject && (
        <LessonStage
          subjectKey={subjectKey}
          onContinue={() =>
            setStage('questions')
          }
          onBack={reset}
          headingRef={headingRef}
        />
      )}

      {stage === 'questions' &&
        currentQuestion && (
          <QuestionStage
            key={currentQuestion.id}
            subjectKey={subjectKey}
            question={currentQuestion}
            questionIndex={questionIndex}
            total={questionTotal}
            onComplete={completeQuestion}
            onBack={reset}
            headingRef={headingRef}
          />
        )}

      {stage === 'result' && subject && (
        <ResultStage
          subjectKey={subjectKey}
          results={results}
          onTryAnother={reset}
          headingRef={headingRef}
        />
      )}
    </div>
  );
}